import { computed, ref } from "vue";
import { useAuthStore } from "~/store/auth-store";
import { useUserStore } from "~/store/user-store";

const IMPERSONATED_ROLE_STORAGE_KEY = "auth-impersonated-role";

const initialImpersonatedRole =
  typeof window !== "undefined"
    ? localStorage.getItem(IMPERSONATED_ROLE_STORAGE_KEY)
    : null;

const impersonatedRole = ref<string | null>(initialImpersonatedRole);

const persistImpersonatedRole = (role: string | null) => {
  if (typeof window === "undefined") {
    return;
  }

  if (!role) {
    localStorage.removeItem(IMPERSONATED_ROLE_STORAGE_KEY);
    return;
  }

  localStorage.setItem(IMPERSONATED_ROLE_STORAGE_KEY, role);
};

export function useRolePermission() {
  const authState = useAuthStore();
  const userStore = useUserStore();

  if (userStore.permissions === undefined) {
    userStore.permissions = [];
  }

  if (typeof window !== "undefined" && userStore.permissions.length === 0) {
    void userStore.ensurePermissionsLoaded();
  }

  const resolvePermissionName = (permission: unknown): string | null => {
    if (typeof permission === "number" && Number.isFinite(permission)) {
      return userStore.permissionNameById(permission) || null;
    }

    if (typeof permission === "string") {
      const trimmed = permission.trim();

      if (trimmed.length === 0) {
        return null;
      }

      if (/^\d+$/.test(trimmed)) {
        return userStore.permissionNameById(parseInt(trimmed, 10)) || null;
      }

      return trimmed;
    }

    return null;
  };

  const allUserRoles = computed(() => {
    return (authState.user?.value?.roles || []) as unknown[];
  });

  const resolveRoleName = (role: unknown): string | null => {
    if (typeof role === "string") {
      const trimmed = role.trim();
      return trimmed.length > 0 ? trimmed.toLowerCase() : null;
    }

    if (role && typeof role === "object") {
      const roleRecord = role as Record<string, unknown>;
      const candidates = [
        roleRecord.name,
        roleRecord.role,
        roleRecord.roleName,
      ];

      for (const candidate of candidates) {
        if (typeof candidate === "string") {
          const trimmed = candidate.trim();
          if (trimmed.length > 0) {
            return trimmed.toLowerCase();
          }
        }
      }
    }

    return null;
  };

  const normalizedUserRoles = computed(() => {
    return allUserRoles.value
      .map((role) => resolveRoleName(role))
      .filter((role): role is string => Boolean(role));
  });

  const isAdminUser = computed(() => {
    return normalizedUserRoles.value.includes("admin");
  });

  const effectiveImpersonatedRole = computed(() => {
    if (!isAdminUser.value || !impersonatedRole.value) {
      return null;
    }

    const trimmedRole = impersonatedRole.value.trim();
    if (trimmedRole.length === 0 || trimmedRole.toLowerCase() === "admin") {
      return null;
    }

    return trimmedRole;
  });

  const userRoles = computed(() => {
    if (effectiveImpersonatedRole.value) {
      return [effectiveImpersonatedRole.value.toLowerCase()];
    }

    return normalizedUserRoles.value;
  });

  const rawUserPermissions = computed(() => {
    return (authState.user?.value?.permissions || []) as unknown[];
  });

  const resolvedRawUserPermissions = computed(() => {
    return rawUserPermissions.value
      .map((permission) => resolvePermissionName(permission))
      .filter((permission): permission is string => Boolean(permission));
  });

  const processedRawUserPermissions = computed(() => {
    return resolvedRawUserPermissions.value.map((permission: string) =>
      permission.split(":")[0].trim().toLowerCase()
    );
  });

  const impersonatedRolePermissionContext = computed(() => {
    if (!effectiveImpersonatedRole.value) {
      return { found: false, fullNames: [] as string[], normalized: [] as string[] };
    }

    const matchedRole = userStore.roles.find(
      (role) =>
        role.name?.trim().toLowerCase() ===
        effectiveImpersonatedRole.value?.trim().toLowerCase()
    );

    if (!matchedRole) {
      return { found: false, fullNames: [] as string[], normalized: [] as string[] };
    }

    const fullNames = (matchedRole.rolePermissions || [])
      .map((permission) => permission.permissionName || "")
      .filter((permissionName) => permissionName.trim().length > 0);

    const normalized = fullNames.map((permissionName) =>
      permissionName.split(":")[0].trim().toLowerCase()
    );

    return { found: true, fullNames, normalized };
  });

  const userPermissions = computed(() => {
    if (effectiveImpersonatedRole.value) {
      return impersonatedRolePermissionContext.value.fullNames;
    }

    return resolvedRawUserPermissions.value;
  });

  const processedUserPermissions = computed(() => {
    if (effectiveImpersonatedRole.value) {
      return impersonatedRolePermissionContext.value.normalized;
    }

    return processedRawUserPermissions.value;
  });

  const computeRoles = (roles: string[]) => {
    const hasAdminOverride = isAdminUser.value && !effectiveImpersonatedRole.value;

    if (hasAdminOverride) {
      return true;
    }

    return roles.length > 0
      ? roles.some((role) => userRoles.value.includes(role.trim().toLowerCase()))
      : true;
  };

  const computePermissions = (permissions: string[]) => {
    if (permissions.length === 0) {
      return true;
    }

    const hasAdminOverride = isAdminUser.value && !effectiveImpersonatedRole.value;

    if (hasAdminOverride) {
      return true;
    }

    return permissions.some((permission) => {
      const required = permission.split(":")[0].trim().toLowerCase();

      return processedUserPermissions.value.some(
        (userPermission) =>
          userPermission === required || userPermission.startsWith(`${required}-`)
      );
    });
  };

  const computeRolesAndPermissions = (
    roles: string[],
    permissions: string[]
  ) => {
    const roleCheck = computeRoles(roles);
    const permissionCheck = computePermissions(permissions);
    return roleCheck || permissionCheck;
  };

  const setImpersonatedRole = (role: string | null) => {
    if (!isAdminUser.value) {
      impersonatedRole.value = null;
      persistImpersonatedRole(null);
      return;
    }

    if (!role) {
      impersonatedRole.value = null;
      persistImpersonatedRole(null);
      return;
    }

    const trimmedRole = role.trim();
    impersonatedRole.value = trimmedRole.length > 0 ? trimmedRole : null;
    persistImpersonatedRole(impersonatedRole.value);

    if (isAdminUser.value && userStore.roles.length === 0) {
      void userStore.fetchRoles(1, 500, "name", "asc", null);
    }
  };

  const clearImpersonatedRole = () => {
    impersonatedRole.value = null;
    persistImpersonatedRole(null);
  };

  return {
    allUserRoles,
    userRoles,
    processedUserPermissions,
    computeRoles,
    computePermissions,
    computeRolesAndPermissions,
    userPermissions,
    isAdminUser,
    impersonatedRole: effectiveImpersonatedRole,
    isImpersonating: computed(() => Boolean(effectiveImpersonatedRole.value)),
    setImpersonatedRole,
    clearImpersonatedRole,
  };
}
